/**
 * Wiedervorlage-Vorschläge: von der Einstellung zum Zeitpunkt.
 *
 * EINE Rechnung für beide Oberflächen — das Anruf-Cockpit (was der Setter wählt) und den
 * Einstellungs-Drawer (die Vorschau neben jedem Eintrag). Zwei Kopien würden auseinanderlaufen,
 * und der Unterschied wäre erst auffällig, wenn ein Rückruf zur falschen Zeit fällig wird.
 *
 * Gespeichert wird immer UTC mit 'Z' (`presetToIso`). Der Server vergleicht das über
 * `_utc_cmp_sql` gegen `datetime('now')`; naive Formen ohne 'Z' würden in JavaScript als LOKALE
 * Zeit gelesen und wären zwei Stunden daneben.
 */
import type { CallbackPreset } from '../services/leadGenApi';

/**
 * Der Zeitpunkt, den ein Vorschlag ab `from` meint.
 *
 * Monate und Tage gehen über setMonth/setDate, damit Monatsenden richtig überlaufen
 * (31.01. + 1 Monat = 03.03. statt eines ungültigen 31.02.). Danach die Stunden, zuletzt `at`.
 *
 * Sommerzeit: gerechnet wird in LOKALER Zeit, „morgen früh 09:00" meint also die Wanduhr. Über
 * eine Zeitumstellung hinweg unterscheidet sich der gespeicherte UTC-Versatz deshalb um eine
 * Stunde von einer naiven Erwartung — das ist die richtige Antwort, nicht der Fehler.
 */
export function presetToDate(p: CallbackPreset, from: Date = new Date()): Date {
  const d = new Date(from.getTime());
  if (p.months) d.setMonth(d.getMonth() + p.months);
  if (p.days) d.setDate(d.getDate() + p.days);
  if (p.hours) d.setHours(d.getHours() + p.hours);
  if (p.at) {
    const [h, m] = p.at.split(':').map(Number);
    if (Number.isFinite(h) && Number.isFinite(m)) d.setHours(h, m, 0, 0);
  }
  d.setSeconds(0, 0);
  return d;
}

/** Derselbe Zeitpunkt als UTC-ISO mit 'Z' — die Form, die gespeichert wird. */
export function presetToIso(p: CallbackPreset, from: Date = new Date()): string {
  return presetToDate(p, from).toISOString();
}

/** Ein `datetime-local`-Eingabefeld erwartet 'YYYY-MM-DDTHH:MM' in LOKALER Zeit. */
export function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Umgekehrt: was im `datetime-local`-Feld steht, als UTC-ISO. Leer/kaputt → null. */
export function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Kurze Beschriftung für die Fällig-Zeile: „heute 14:00" · „morgen 09:00" · „14.08. 10:00". */
export function labelFor(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const uhr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const tage = Math.round(
    (new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000,
  );
  if (tage === 0) return `heute ${uhr}`;
  if (tage === 1) return `morgen ${uhr}`;
  if (tage === -1) return `gestern ${uhr}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${uhr}`;
}

/**
 * Aus einem frei gewählten Zeitpunkt einen speicherbaren Vorschlag machen („+ speichern").
 * Ganze Tage werden als Tage ausgedrückt (mit der Uhrzeit als `at`), alles unter einem Tag als
 * Stunden — sonst hiesse ein Vorschlag „in 0 Tagen" und träfe morgen etwas anderes als heute.
 */
export function presetFromDate(target: Date, now: Date = new Date()): Omit<CallbackPreset, 'default'> {
  const pad = (n: number) => String(n).padStart(2, '0');
  const tage = Math.round(
    (new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime()
      - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000,
  );
  const uhr = `${pad(target.getHours())}:${pad(target.getMinutes())}`;
  if (tage <= 0) {
    const stunden = Math.max(1, Math.round((target.getTime() - now.getTime()) / 3_600_000));
    return { label: `in ${stunden}h`, months: 0, days: 0, hours: stunden, at: null };
  }
  const label = tage === 1 ? `morgen ${uhr}`
    : tage % 7 === 0 ? `in ${tage / 7} Wo · ${uhr}`
      : `in ${tage} Tagen · ${uhr}`;
  return { label, months: 0, days: tage, hours: 0, at: uhr };
}
